import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import {
  MibButtonDirective,
  MibModalComponent,
  MibUploadComponent,
  MibUploadPicked,
  MibUploadRemoval,
} from '@medinbox/angular';
import { CaseLibraryService } from '../case-library.service';

/** No ceiling of our own: what a case may weigh is the storage's business, not this screen's. */
const NO_SIZE_LIMIT = Number.POSITIVE_INFINITY;

/** Holds nothing: the list and the transfers are the library service's. Closing never cancels. */
@Component({
  selector: 'mai-upload-dialog',
  imports: [MibModalComponent, MibUploadComponent, MibButtonDirective],
  templateUrl: './upload-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadDialog {
  private readonly dialogRef = inject<DialogRef>(DialogRef);
  private readonly library = inject(CaseLibraryService);

  protected readonly files = this.library.files;
  protected readonly noSizeLimit = NO_SIZE_LIMIT;

  constructor() {
    this.dialogRef.closed.subscribe(() => this.library.clearFinished());
  }

  /* Nothing to be done with an empty dialog: closing it is what the cross and Cancel are for. */
  protected readonly started = computed(() => this.files().length > 0);

  /*
   A failed file never holds the button: it has finished, badly, and there is nothing left to wait
   for. Removing the last one that was still going frees it too, since the row leaves the list.
  */
  protected readonly working = computed(() =>
    this.files().some((file) => file.state === 'uploading'),
  );

  /*
   Refusals are drawn too: the picker filters on `accept`, a dropped file does not, so anything but
   a video reaches us as rejected and would otherwise vanish on landing.
  */
  protected picked(picked: MibUploadPicked): void {
    this.library.startUpload(picked.accepted);
    this.library.showRejected(picked.rejected);
  }

  /* The row is ours to drop: the component never touches its own list. */
  protected removed({ file }: MibUploadRemoval): void {
    if (file.file) {
      this.library.cancelUpload(file.file);
    }
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
